import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap border border-transparent font-heading text-sm font-semibold leading-tight transition-colors disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          'border-primary bg-primary text-primary-foreground hover:bg-steel-600 active:bg-steel-700',
        destructive:
          'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border-border bg-transparent hover:bg-foreground/7 active:bg-foreground/14',
        secondary: 'border-border bg-transparent hover:bg-foreground/7 active:bg-foreground/14',
        ghost: 'text-steel-700 hover:bg-steel/10 active:bg-steel/18',
        link: 'text-steel-700 font-sans font-normal underline-offset-3 hover:underline',
      },
      size: {
        default: 'h-control px-3.5 has-[>svg]:px-3',
        sm: 'h-8 gap-1 px-2.5 has-[>svg]:px-2',
        lg: 'h-12 px-5 text-base has-[>svg]:px-4',
        icon: 'size-control',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
